import * as React from "react";
import styles = require("./style.less");
import {Cancelable, debounce, throttle} from "lodash";
import EventListener, {withOptions} from "react-event-listener";
import * as cn from "classnames";

export interface MeasureScrollableTableProps {
    headers: React.ReactNode[];
    firstColumn: React.ReactNode[];
    rows: React.ReactNode[][];
    empty: React.ReactNode;
    top: string[];
    debug: string;
    measurements?: MeasureScrollableTableState;
    onMeasured?(state: MeasureScrollableTableState): void;
    onWindowResized?(state: MeasureScrollableTableState): void;
}

export interface MeasureScrollableTableState {
    columnsWidths?: number[];
    rowHeights?: number[];
    dataContainerTopMargin?: number;
    headersContainerWidth?: number;
    headersOverflowContainerWidth?: number;
}

export default class MeasureScrollableTable extends React.Component<MeasureScrollableTableProps, MeasureScrollableTableState> {

    private headersContainer: HTMLDivElement | undefined;
    private headersOverflowContainer: HTMLDivElement | undefined;
    private firstColumnContainer: HTMLDivElement | undefined;
    private scrollableContainer: HTMLDivElement | undefined;
    private dataContainer: HTMLDivElement | undefined;
    private tableContainer: HTMLTableElement | undefined;
    private onWindowScroll: (() => void) & Cancelable;
    private onScrollTableHeaderContainer: (() => void) & Cancelable;
    private onScrollTableDataContainer: (() => void) & Cancelable;
    private onWindowResize: (() => void) & Cancelable;
    private offsetHeight: number = 0;
    private stickyElements: Element[] = [];
    private tableTop: number = undefined;
    private tableHeight = 0;
    private onMeasured: (state: MeasureScrollableTableState) => void;
    private onWindowResized: (state: MeasureScrollableTableState) => void;
    private handleWindowScroll = debounce(() => {
        const headerContainerTop = this.tableTop - (this.props.measurements.dataContainerTopMargin || 0) - window.pageYOffset - this.offsetHeight;
        let top = 0;

        if (headerContainerTop < 0) {
            top = Math.abs(headerContainerTop);

            // Don't move the header all the way to the bottom otherwise user can't see last row
            const maxAllowed = this.tableHeight - 100;
            if (maxAllowed < 0) {
                top = 0;
            } else if (top > maxAllowed) {
                top = maxAllowed;
            }
        }

        this.headersOverflowContainer.style.top = `${top}px`;
        this.headersContainer.style.borderBottom = top > 0 ? "solid 0.0625rem #e0e0e0" : "";
        this.headersOverflowContainer.style.backgroundColor = top > 0 ? "#fafafa" : "#ffffff";
        this.headersOverflowContainer.style.boxShadow = top > 0 ? "0px 2px 3px -2px rgba(0,0,0,0.6)" : "";
    }, 50);

    private handleTableHeaderScroll = throttle(() => {
        this.handleTableScroll(() => this.scrollableContainer.scrollLeft = this.headersOverflowContainer.scrollLeft);
    }, 50);

    private handleTableDataScroll = throttle(() => {
        this.handleTableScroll(() => this.headersOverflowContainer.scrollLeft = this.scrollableContainer.scrollLeft);
    }, 50);

    private handleWindowResize = debounce(() => {
        this.setOffsetHeight();

        if (this.dataContainer) {
            const dataContainerRect = this.getBoundingClientRectRounded(this.dataContainer);
            if (this.onWindowResized) {
                this.onWindowResized({
                    ...this.props.measurements,
                    headersOverflowContainerWidth: dataContainerRect.width
                });
            }
        }
        if (this.scrollableContainer) {
            const tableRect = this.getBoundingClientRectRounded(this.scrollableContainer);
            this.tableTop = tableRect.top + window.pageYOffset;
            this.tableHeight = tableRect.height;
        }

        this.handleWindowScroll();
    }, 1000);

    constructor(props: MeasureScrollableTableProps) {
        super(props);

        this.onWindowScroll = this.handleWindowScroll;
        this.onScrollTableHeaderContainer = this.handleTableHeaderScroll;
        this.onScrollTableDataContainer = this.handleTableDataScroll;
        this.onWindowResize = this.handleWindowResize;
        props.top.forEach(id => {
            const element = document.getElementById(id);

            if (element) {
                this.stickyElements.push(element);
            }
        });

        this.onMeasured = props.onMeasured;
        this.onWindowResized = props.onWindowResized;

        this.setOffsetHeight();

        this.state = {
            columnsWidths: undefined,
            rowHeights: undefined,
            dataContainerTopMargin: undefined,
            headersContainerWidth: undefined,
            headersOverflowContainerWidth: undefined
        };
    }

    componentDidMount() {
        if (this.isMeasuring()) {
            this.adjustLayout();
        } else {
            this.setOffsetHeight();

            const tableRect = this.getBoundingClientRectRounded(this.scrollableContainer);
            this.tableTop = tableRect.top + window.pageYOffset;
            this.tableHeight = tableRect.height;
            this.headersOverflowContainer.addEventListener("scroll", this.onScrollTableHeaderContainer, false);
            this.scrollableContainer.addEventListener("scroll", this.onScrollTableDataContainer, false);
        }
    }

    componentWillReceiveProps(nextProps: MeasureScrollableTableProps) {
        if (this.isMeasuring()) {
            this.setState({
                columnsWidths: undefined,
                rowHeights: undefined,
                dataContainerTopMargin: undefined,
                headersContainerWidth: undefined,
                headersOverflowContainerWidth: undefined
            });
        }
    }

    componentDidUpdate() {
        if (this.state.columnsWidths === undefined) {
            this.adjustLayout();
            return;
        }

        if (this.state.headersOverflowContainerWidth === undefined) {
            const dataContainerRect = this.getBoundingClientRectRounded(this.dataContainer);
            this.setState({headersOverflowContainerWidth: dataContainerRect.width});
            return;
        }

        if (this.state.dataContainerTopMargin === undefined) {
            this.setState({
                dataContainerTopMargin: this.getBoundingClientRectRounded(this.headersOverflowContainer).height
            }, () => {
                if (this.isMeasuring()) {
                    this.onMeasured(this.state);
                }
            });
            return;
        }
    }

    componentWillUnmount() {
        this.onScrollTableHeaderContainer.cancel();
        this.onScrollTableDataContainer.cancel();
        this.headersOverflowContainer.removeEventListener("scroll", this.onScrollTableHeaderContainer, false);
        this.scrollableContainer.removeEventListener("scroll", this.onScrollTableDataContainer, false);
        this.onWindowScroll.cancel();
        this.onWindowResize.cancel();
    }

    render() {
        const measurements = this.props.measurements ? this.props.measurements : this.state;
        return <div className={styles.scrollableTableContainer}>
            {!this.isMeasuring() && <EventListener
                target={window}
                onScroll={withOptions(this.onWindowScroll, {passive: true, capture: false})}
                onResize={withOptions(this.onWindowResize, {passive: true, capture: false})}
            />}
            <div ref={el => this.firstColumnContainer = el} className={styles.firstColumnContainer}>
                <table cellPadding={0} cellSpacing={0}>
                    <tbody>
                    {this.props.firstColumn.map((cell, idx) =>
                        <tr key={idx}>
                            <td className={cn({[styles.border]: idx !== 0})}>
                                <div className={cn(styles.headerCell, {[styles.highlightColumn]: idx === 0})}
                                     style={{
                                         height: idx === 0
                                             ? this.getValue(measurements.dataContainerTopMargin)
                                             : this.arrayValue(measurements.rowHeights, idx)
                                     }}>
                                    {cell}
                                </div>
                            </td>
                        </tr>)}
                    </tbody>
                </table>
                <div className={styles.scrollbarPadding}/>
            </div>
            <div className={styles.rightContainer}>
                <div ref={el => this.headersOverflowContainer = el}
                     className={styles.headersOverflowContainer}
                     style={{width: this.getValue(measurements.headersOverflowContainerWidth)}}>
                    <div ref={el => this.headersContainer = el}
                         className={styles.headersContainer}
                         style={{width: this.getValue(measurements.headersContainerWidth)}}>
                        {this.props.headers.map((cell, idx) =>
                            <div key={idx}
                                 className={styles.columnHead}
                                 style={{
                                     height: this.arrayValue(measurements.rowHeights, 0),
                                     width: this.arrayValue(measurements.columnsWidths, idx + 1),
                                 }}>
                                {cell}
                            </div>)}
                    </div>
                </div>
                <div ref={el => this.dataContainer = el} className={styles.dataContainer}
                     style={{marginTop: this.getValue(measurements.dataContainerTopMargin)}}>
                    <div className={styles.scrollableContainer} ref={el => this.scrollableContainer = el}>
                        <table ref={el => this.tableContainer = el} cellPadding={0} cellSpacing={0}>
                            <tbody>
                            {this.props.rows.length === 0 && <tr>
                                <td className={styles.emptyCell}>{this.props.empty}</td>
                                <td className={styles.spacerCell}/>
                            </tr>}
                            {this.props.rows.map((row, rowIdx) => <tr key={rowIdx}>
                                {row.map((cell, colIdx) => <td key={`${rowIdx}=${colIdx}`} className={styles.border}>
                                    <div className={styles.centerCell}
                                         style={{
                                             height: this.arrayValue(measurements.rowHeights, rowIdx + 1),
                                             width: this.arrayValue(measurements.columnsWidths, colIdx + 1),
                                         }}>
                                        {cell}
                                    </div>
                                </td>)}
                                <td className={styles.spacerCell}/>
                            </tr>)}
                            </tbody>
                        </table>
                        <div className={styles.scrollbarPadding}/>
                    </div>
                    <div className={styles.hideScrollbarPanel}/>
                </div>
            </div>
        </div>;
    }

    private arrayValue = (value: number[] | undefined, idx: number) => {
        if (value) {
            return value[idx];
        }

        return "initial";
    }

    private getValue = (value: number | undefined) => {
        if (value) {
            return value;
        }

        return "initial";
    }

    private setOffsetHeight = () => {
        if (this.stickyElements.length > 0) {
            this.offsetHeight = this.stickyElements.reduce((p, c) => p + this.getBoundingClientRectRounded(c).height, 0);
        }
    }

    private adjustLayout = () => {
        const {columnsWidths, rowHeights} = this.measureCells();

        const totalColumnsWidth = columnsWidths.slice(1).reduce((p, c) => p + c, 0);
        this.setState({
            columnsWidths,
            rowHeights,
            headersContainerWidth: totalColumnsWidth
        });
    }

    private measureCells = () => {
        const columnsWidths: number[] = [];
        const rowHeights: number[] = [];

        // 1. measure all firstcolumns including table title (0, 0)
        // tslint:disable-next-line:prefer-for-of
        const firstColumnContainerTable = this.firstColumnContainer.children[0] as HTMLTableElement;
        for (let idx = 0; idx < firstColumnContainerTable.rows.length; idx++) {
            const cell = firstColumnContainerTable.rows[idx].cells[0].children[0] as HTMLElement;
            const cellRect = this.getBoundingClientRectRounded(cell);

            if (idx === 0) {
                columnsWidths.push(0); //Not used
            }

            rowHeights.push(cellRect.height);
        }

        // 2. measure all headers
        // tslint:disable-next-line:prefer-for-of
        for (let idx = 0; idx < this.headersContainer.children.length; idx++) {
            const cell = this.headersContainer.children[idx] as HTMLElement;
            const cellRect = this.getBoundingClientRectRounded(cell);
            columnsWidths.push(cellRect.width);
            rowHeights[0] = Math.max(rowHeights[0], cellRect.height);
        }

        // 3. measure data cells
        if (this.props.rows.length > 0 && this.props.rows[0].length > 0) {
            const yAxisLength = this.tableContainer.rows.length;
            const xAxisLength = this.tableContainer.rows[0].cells.length - 1;
            const calcCoordIdx = (idx: number, maxIdx: number) => idx >= maxIdx ? maxIdx - 1 : idx;
            const maxLength = Math.max(yAxisLength, xAxisLength);

            for (let coordIdx = 0; coordIdx < maxLength; coordIdx++) {
                const x = calcCoordIdx(coordIdx, xAxisLength);
                const y = calcCoordIdx(coordIdx, yAxisLength);
                const cell = this.tableContainer.rows[y].cells[x] as HTMLElement;
                const cellRect = this.getBoundingClientRectRounded(cell);

                rowHeights[y + 1] = Math.max(rowHeights[y + 1], cellRect.height);
                columnsWidths[x + 1] = Math.max(columnsWidths[x + 1], cellRect.width);
            }
        }

        return {columnsWidths, rowHeights};
    }

    private handleTableScroll = (action: () => void) => {
        if (this.props.rows.length > 0) {
            action();
            this.firstColumnContainer.style.boxShadow = this.scrollableContainer.scrollLeft > 0 ? "3px 2px 3px -2px rgba(0,0,0,0.6)" : "";
            this.firstColumnContainer.style.backgroundColor = this.scrollableContainer.scrollLeft > 0 ? "#fafafa" : "";
        }
    }

    /// Need to round sizes otherwise different browsers do different things
    private getBoundingClientRectRounded(element: Element) {
        const rect = element.getBoundingClientRect();
        return {
            width: Math.round(rect.width),
            height: Math.round(rect.height),
            top: Math.round(rect.top),
            bottom: Math.round(rect.bottom)
        };
    }

    private isMeasuring = () => {
        return !!this.onMeasured;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/areas/projects/components/ProjectDashboard/MeasureScrollableTable.tsx